/**
 * Starter prompts — `bimgo-ai-web`'s `StarterPromptPanel`, now fed by the
 * open document (item BH5).
 *
 * Closed, it is one quiet line — the way in. Opened, it asks the agent for
 * three prompts written about the model that is open (`/document-context/
 * starter-prompts`: understand · inspect · export) and shows them as buttons
 * that fill the composer. The agent answers with AI-written copy when it can
 * and deterministic copy when it cannot, and says which (`fallbackReason`);
 * the panel repeats that as a notice, exactly as the old panel did. With no
 * bound instance, no project, or a failed call, the generic examples stand in
 * — labelled as generic, never passed off as being about this model.
 */
import { useEffect, useRef, useState } from "react";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  fetchStarterPrompts,
  starterNoticeKey,
  StarterPromptError,
  type StarterPrompt,
} from "../../lib/starterPrompts";
import type { CurrentModel, ReasoningEffort } from "../../lib/models";
import { Spinner } from "../settings/Spinner";

const GENERIC_KEYS = [
  "starterPrompts.generic.overview",
  "starterPrompts.generic.count",
  "starterPrompts.generic.export",
] as const;

function ArrowIcon() {
  return (
    <svg
      width="15"
      height="15"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14M12 5l7 7-7 7" />
    </svg>
  );
}

type Loaded =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "context"; prompts: StarterPrompt[]; noticeKey: string | null }
  /**
   * The generic examples, and **which sentence explains them**.
   *
   * One sentence used to cover every way of getting here, which meant the single most
   * common cause — no BIM model open — was described as "the current model is
   * unavailable": the wrong model (people read it as the LLM), and no mention of the
   * one action that fixes it.
   */
  | { kind: "generic"; noticeKey: string };

export type StarterSelection = { kind: "prompt"; prompt: StarterPrompt } | { kind: "text"; text: string };

export function StarterPromptPanel({
  disabled,
  onSelect,
  token,
  orgId,
  projectId,
  targetSessionId,
  model,
  reasoningEffort,
}: {
  disabled: boolean;
  /**
   * Fills the composer rather than sending: the user may want to edit first.
   * A context prompt arrives whole so the composer can insert its mention as
   * a chip; a generic example is just text.
   */
  onSelect: (selection: StarterSelection) => void;
  token: string | null;
  orgId: string | null;
  projectId: string | null;
  /** The bound instance — whose document the prompts are written about. */
  targetSessionId: string | null;
  /** The composer's model, so these prompts are written by the model the user chose (#127). */
  model: CurrentModel | null;
  /** The composer's reasoning chip, when it applies to that model. */
  reasoningEffort?: ReasoningEffort;
}) {
  const { t, locale } = useLocale();
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<Loaded>({ kind: "idle" });

  /**
   * Read at fetch time, deliberately **not** effect dependencies.
   *
   * Generating prompts is a billed model call. With these in the dependency list,
   * moving the model picker or the reasoning chip while the panel is open would spend
   * again — and a user comparing models would pay per comparison for prompts they did
   * not ask to be rewritten. The selection that was live when the panel opened is the
   * one that writes them; changing it applies the next time they are fetched.
   */
  const modelRef = useRef(model);
  modelRef.current = model;
  const effortRef = useRef(reasoningEffort);
  effortRef.current = reasoningEffort;

  useEffect(() => {
    if (!open) return;
    if (!token || !orgId || !projectId || !targetSessionId) {
      /**
       * No request is made here at all, which is worth knowing when debugging: an empty
       * agent log does not mean the agent failed, it means nothing asked it.
       *
       * A missing Revit session is singled out because it is the only one of the four
       * the user can act on, and in practice it is nearly always the one. The other
       * three (no token, org or project) leave the composer unusable anyway, so a
       * sentence about opening Revit would be misdirection.
       */
      setState({
        kind: "generic",
        noticeKey: targetSessionId ? "starterPrompts.genericFallback" : "starterPrompts.openModelFallback",
      });
      return;
    }
    const controller = new AbortController();
    setState({ kind: "loading" });
    fetchStarterPrompts(
      {
        token,
        organizationId: orgId,
        projectId,
        locale: locale === "zh" ? "zh" : "en",
        targetSessionId,
        model: modelRef.current,
        ...(effortRef.current ? { reasoningEffort: effortRef.current } : {}),
        signal: controller.signal,
      },
    )
      .then((response) => {
        if (controller.signal.aborted) return;
        setState({ kind: "context", prompts: response.prompts, noticeKey: starterNoticeKey(response) });
      })
      .catch((error: unknown) => {
        // Every one of these is "the generic examples, labelled as such", not an error.
        // But 404 and 503 mean something specific and actionable: the agent has no
        // document context for that year (404) or no Revit year to read at all (503) —
        // which is Revit running with **no BIM model open**, the same situation as a
        // missing session above and the same one sentence to say about it. Anything
        // else (a refused credit, a 500) is not the user's to fix.
        const status = error instanceof StarterPromptError ? error.status : 0;
        const noticeKey =
          status === 404 || status === 503
            ? "starterPrompts.openModelFallback"
            : "starterPrompts.genericFallback";
        if (!controller.signal.aborted) setState({ kind: "generic", noticeKey });
      });
    return () => controller.abort();
  }, [open, token, orgId, projectId, targetSessionId, locale]);

  if (!open) {
    return (
      <section className="starter" aria-label={t("starterPrompts.sectionLabel")}>
        <button
          type="button"
          className="starter-cta"
          disabled={disabled}
          aria-label={t("starterPrompts.cta")}
          onClick={() => setOpen(true)}
        >
          <span className="starter-cta-text">
            {t("starterPrompts.ctaPrefix")}
            <img
              className="starter-logo"
              src="/bimgo-logo-horizontal-color.png"
              alt=""
              aria-hidden="true"
            />
            {t("starterPrompts.ctaSuffix")}
          </span>
          <ArrowIcon />
        </button>
      </section>
    );
  }

  return (
    <section className="starter is-open" aria-label={t("starterPrompts.sectionLabel")}>
      {/* `idle` reads as loading, not as generic: opening the panel renders once before
          the effect runs, and falling through to the generic branch flashed the general
          examples for a frame on the way to real ones. */}
      {state.kind === "loading" || state.kind === "idle" ? (
        <p className="starter-note" role="status">
          <Spinner size={13} /> {t("starterPrompts.loading")}
        </p>
      ) : state.kind === "context" ? (
        <>
          {state.noticeKey ? (
            <p className="starter-note" role="status">
              {t(state.noticeKey)}
            </p>
          ) : null}
          {state.prompts.map((prompt) => (
            <button
              key={prompt.id}
              type="button"
              className="starter-prompt"
              disabled={disabled}
              data-intent={prompt.intent}
              onClick={() => onSelect({ kind: "prompt", prompt })}
            >
              {prompt.beforeText}
              <span className="starter-mention">@{prompt.mention.label}</span>
              {prompt.afterText}
            </button>
          ))}
        </>
      ) : (
        <>
          {/* Said plainly rather than silently: these are the general examples,
              not ones written about the model that is open. */}
          <p className="starter-note" role="status">
            {t(state.noticeKey)}
          </p>
          {GENERIC_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className="starter-prompt"
              disabled={disabled}
              onClick={() => onSelect({ kind: "text", text: t(key) })}
            >
              {t(key)}
            </button>
          ))}
        </>
      )}
    </section>
  );
}
